/** OPS-1 usage tracking: which features reps use, on which clients, VINs and buyers.
 *
 *  `track()` is fire-and-forget: events are buffered and sent in batches (every few seconds, or
 *  sooner when the buffer fills), and flushed with a keepalive request when the page is hidden.
 *  The server stamps who sent them. Nothing here can throw into, or slow, the caller.
 */
import type { UsageEventPayload } from '../api/siftApiClient';

export type UsageView = 'main' | 'client_view' | 'sales_view' | 'broker_view' | 'mia' | 'admin';

export interface UsageIds {
  seller_uid?: string | number | null;
  vin?: string | null;
  buyer_uid?: string | number | null;
}

type Transport = (events: UsageEventPayload[], keepalive: boolean) => Promise<void>;

const FLUSH_DELAY_MS = 5000;
const MAX_BUFFER = 25;
/** The route accepts 100 per request; also caps what one hidden tab can lose. */
const MAX_BATCH = 100;

let buffer: UsageEventPayload[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let transport: Transport | null = null;
let defaultLoaded = false;
let listening = false;

const appVersion = (): string | undefined => {
  try {
    return (import.meta.env.VITE_APP_VERSION as string | undefined) || import.meta.env.MODE;
  } catch {
    return undefined;
  }
};

/** Tests and the default both go through here. Unit tests leave it unset, so nothing is sent. */
export function setUsageTransport(next: Transport | null): void {
  transport = next;
  defaultLoaded = true;
}

async function resolveTransport(): Promise<Transport | null> {
  if (!defaultLoaded) {
    defaultLoaded = true;
    if (import.meta.env.MODE !== 'test') {
      const { siftApiClient } = await import('../api/siftApiClient');
      transport = (events, keepalive) => siftApiClient.sendUsageEvents(events, keepalive);
    }
  }
  return transport;
}

export async function flushUsage(keepalive = false): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!buffer.length) return;
  const send = await resolveTransport();
  if (!send) {
    buffer = [];
    return;
  }
  while (buffer.length) {
    const batch = buffer.splice(0, MAX_BATCH);
    try {
      await send(batch, keepalive);
    } catch {
      // Telemetry is best effort: a failed batch is dropped, never retried into the UI.
    }
  }
}

function listen(): void {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushUsage(true);
  });
  window.addEventListener('pagehide', () => { void flushUsage(true); });
}

const idText = (value: string | number | null | undefined): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

/** Record one interaction. `feature` names the surface (vin_details, basket, nav...), `action`
 *  what was done (open, add_all, take_me_there...). Keep `meta` small: it is capped at 2 KB. */
export function track(
  view: UsageView,
  feature: string,
  action: string,
  ids: UsageIds = {},
  meta?: Record<string, unknown>,
): void {
  try {
    listen();
    buffer.push({
      view,
      feature,
      action,
      seller_uid: idText(ids.seller_uid),
      vin: idText(ids.vin),
      buyer_uid: idText(ids.buyer_uid),
      ...(meta ? { meta } : {}),
      ts: new Date().toISOString(),
      app_version: appVersion(),
    });
    if (buffer.length >= MAX_BUFFER) {
      void flushUsage();
    } else if (!timer) {
      timer = setTimeout(() => { void flushUsage(); }, FLUSH_DELAY_MS);
    }
  } catch {
    // Never let telemetry break the interaction it is recording.
  }
}

// ---------------------------------------------------------------- every click, every view
//
// One document-level listener records every click on something interactive (buttons, links,
// tabs, checkboxes, menu items, clickable cards and rows) and every dropdown change, in
// whichever view is showing. What it was ABOUT comes from the page:
//   * the nearest ancestor carrying data-usage-seller-uid / data-usage-vin / data-usage-buyer-uid
//     (cards and table rows), field by field;
//   * else the innermost open pop-out (pushUsageContext), so a click inside a client pop-out
//     or VIN details is tied to that client / VIN;
//   * the feature is the nearest data-usage-section, else the pop-out's section, else "page".
// Typed text is never captured: only an element's label, and a dropdown's chosen option.

export const USAGE_ATTR = {
  seller: 'data-usage-seller-uid',
  vin: 'data-usage-vin',
  buyer: 'data-usage-buyer-uid',
  section: 'data-usage-section',
  label: 'data-usage-label',
  ignore: 'data-usage-ignore',
} as const;

let currentView: UsageView = 'main';
const contexts: Array<{ section: string; ids: UsageIds }> = [];
let capturing = false;

export function setUsageView(view: UsageView): void {
  currentView = view;
}

/** While a pop-out is open, clicks inside it are about its client / VIN. Returns the undo. */
export function pushUsageContext(section: string, ids: UsageIds): () => void {
  const entry = { section, ids };
  contexts.push(entry);
  return () => {
    const i = contexts.lastIndexOf(entry);
    if (i >= 0) contexts.splice(i, 1);
  };
}

const INTERACTIVE = [
  'button', 'a[href]', 'summary', 'input[type="checkbox"]', 'input[type="radio"]',
  '[role="button"]', '[role="tab"]', '[role="menuitem"]', '[role="option"]', '[role="switch"]',
  '[role="checkbox"]', '[role="link"]', 'tr[class*="row"]',
].join(', ');

const clip = (text: string | null | undefined, max = 80): string =>
  (text ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

function labelOf(el: Element): string {
  const explicit = el.getAttribute(USAGE_ATTR.label) || el.getAttribute('aria-label') || el.getAttribute('title');
  if (explicit) return clip(explicit);
  if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
    const byFor = el.id ? el.ownerDocument.querySelector(`label[for="${CSS.escape(el.id)}"]`) : null;
    const wrapping = el.closest('label');
    return clip((byFor ?? wrapping)?.textContent || el.name || el.type);
  }
  return clip(el.textContent);
}

function attrUp(el: Element, attr: string): string | null {
  return el.closest(`[${attr}]`)?.getAttribute(attr) ?? null;
}

/** What a click on `el` should be recorded as. Exported for tests. */
export function describeElement(el: Element): { feature: string; ids: UsageIds; label: string; kind: string } {
  const top = contexts[contexts.length - 1];
  const ids: UsageIds = {
    seller_uid: attrUp(el, USAGE_ATTR.seller) ?? top?.ids.seller_uid ?? null,
    vin: attrUp(el, USAGE_ATTR.vin) ?? top?.ids.vin ?? null,
    buyer_uid: attrUp(el, USAGE_ATTR.buyer) ?? top?.ids.buyer_uid ?? null,
  };
  const feature = attrUp(el, USAGE_ATTR.section) ?? top?.section ?? 'page';
  const kind = el.getAttribute('role') || el.tagName.toLowerCase();
  return { feature, ids, label: labelOf(el), kind };
}

function onClick(ev: Event): void {
  try {
    const target = ev.target as Element | null;
    const el = target?.closest?.(INTERACTIVE);
    if (!el || el.closest(`[${USAGE_ATTR.ignore}]`)) return;
    const d = describeElement(el);
    const meta: Record<string, unknown> = { label: d.label, el: d.kind };
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) meta.checked = el.checked;
    track(currentView, d.feature, 'click', d.ids, meta);
  } catch {
    // never let telemetry break a click
  }
}

function onChange(ev: Event): void {
  try {
    const el = ev.target;
    if (!(el instanceof HTMLSelectElement) || el.closest(`[${USAGE_ATTR.ignore}]`)) return;
    const d = describeElement(el);
    const chosen = Array.from(el.selectedOptions).map((o) => clip(o.textContent, 60));
    track(currentView, d.feature, 'change', d.ids, { label: d.label, el: 'select', value: chosen.join(', ') });
  } catch {
    // never let telemetry break a change
  }
}

/** Install the listeners once (capture phase, so a handler that stops propagation is still seen). */
export function startClickCapture(doc: Document = document): void {
  if (capturing) return;
  capturing = true;
  doc.addEventListener('click', onClick, true);
  doc.addEventListener('change', onChange, true);
}

/** Test helper: what is waiting to be sent. */
export function pendingUsage(): UsageEventPayload[] {
  return [...buffer];
}
